/**
 * Разбор ошибки логина (DEV-1775).
 *
 * Причина отказа берётся из машиночитаемого `details[].reason` (ErrorInfo от
 * auth), затем из текста сервера, затем — общий текст. Матчинга по тексту
 * сообщения нет: текст локализован и может меняться.
 */
import { isAxiosError } from 'axios'

// grpc-gateway сериализует details как массив объектов с "@type".
const ERROR_INFO_TYPE_SUFFIX = 'google.rpc.ErrorInfo'

type ReasonMessage = { key: string; hintKey?: string }

const MESSAGE_BY_REASON: Record<string, ReasonMessage> = {
  user_deactivated: { key: 'agents.login.errors.deactivated' },
  // Подсказка ведёт к «Забыли пароль?» — путь восстановления уже на форме.
  user_not_activated: {
    key: 'agents.login.errors.notActivated',
    hintKey: 'agents.login.errors.notActivatedHint',
  },
  invalid_credentials: { key: 'agents.login.error' },
}

const GENERIC_KEY = 'agents.login.errors.generic'
const INVALID_CREDENTIALS_KEY = 'agents.login.error'

/** Результат разбора: ключ перевода (иногда с подсказкой) или текст сервера. */
export type LoginErrorMessage = ReasonMessage | { text: string }

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

/** Достаёт reason из ErrorInfo в details; details может не быть вовсе. */
const extractReason = (data: unknown): string | null => {
  if (!isRecord(data) || !Array.isArray(data.details)) {
    return null
  }

  for (const detail of data.details) {
    if (!isRecord(detail) || typeof detail.reason !== 'string' || !detail.reason) {
      continue
    }
    const type = detail['@type']
    if (typeof type === 'string' && !type.endsWith(ERROR_INFO_TYPE_SUFFIX)) {
      continue
    }
    return detail.reason
  }

  return null
}

const extractServerMessage = (data: unknown): string | null => {
  if (isRecord(data) && typeof data.message === 'string' && data.message.trim() !== '') {
    return data.message
  }

  return null
}

/**
 * Определяет, что показать на форме входа. Приоритет: известный reason →
 * 401 как «неверный email или пароль» → текст сервера → общий текст.
 */
export function resolveLoginError(error: unknown): LoginErrorMessage {
  if (!isAxiosError(error) || !error.response) {
    return { key: GENERIC_KEY }
  }

  const reason = extractReason(error.response.data)
  const messageByReason = reason === null ? undefined : MESSAGE_BY_REASON[reason]
  if (messageByReason) {
    return messageByReason
  }

  if (error.response.status === 401) {
    return { key: INVALID_CREDENTIALS_KEY }
  }

  const serverMessage = extractServerMessage(error.response.data)
  if (serverMessage !== null) {
    return { text: serverMessage }
  }

  return { key: GENERIC_KEY }
}
